import { Upload } from 'lucide-react';
import type { DeploymentResource } from '../api/types';
import { CopyButton } from '../components/ui';
import { deployCommands, deployTarget } from '../lib/triggers';
import { strings } from '../strings';

/**
 * What the user's pipeline needs to deploy a function app's code (spec 13, 4.2): the app name, resource
 * group and subscription, example commands and the roles the pipeline's identity needs.
 */
export function DeployBox({
  resourceId,
  resource,
  hasDeployment,
}: {
  resourceId: string;
  /** The function as deployed, when a deployment is selected and has it. */
  resource: DeploymentResource | undefined;
  hasDeployment: boolean;
}) {
  const target = deployTarget(resource);
  const commands = deployCommands(target);
  const value = (text: string | null) => text ?? strings.common.dash;
  return (
    <section
      aria-label={`${strings.deployBox.title}: ${resourceId}`}
      data-testid={`deploy-box-${resourceId}`}
      className="card mt-3 p-3"
    >
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Upload aria-hidden className="size-4" />
        {strings.deployBox.title}
      </h3>
      <p className="help mb-2">{strings.deployBox.help}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
        <dt className="font-medium">{strings.deployBox.name}</dt>
        <dd className="font-mono break-all">{value(target.name)}</dd>
        <dt className="font-medium">{strings.deployBox.resourceGroup}</dt>
        <dd className="font-mono break-all">{value(target.resourceGroup)}</dd>
        <dt className="font-medium">{strings.deployBox.subscription}</dt>
        <dd className="font-mono break-all">{value(target.subscriptionId)}</dd>
      </dl>
      {!resource && (
        <p className="help mt-1">
          {hasDeployment ? strings.deployBox.notDeployed : strings.deployBox.selectDeployment}
        </p>
      )}
      <h4 className="mt-2 text-xs font-semibold">{strings.deployBox.commands}</h4>
      <ul className="space-y-1">
        {commands.map((command) => (
          <li key={command} className="flex items-start gap-1">
            <code className="min-w-0 flex-1 rounded bg-slate-100 px-1.5 py-1 text-xs break-all dark:bg-slate-800">
              {command}
            </code>
            <CopyButton value={command} label={strings.deployBox.copyCommand} />
          </li>
        ))}
      </ul>
      <h4 className="mt-2 text-xs font-semibold">{strings.deployBox.roles}</h4>
      <ul className="list-disc pl-5 text-xs">
        <li>{strings.deployBox.roleFunction}</li>
        <li>{strings.deployBox.roleContainer}</li>
      </ul>
    </section>
  );
}
